import {useEffect} from 'react';
import {useSelector} from 'react-redux';
import * as Yup from 'yup';
import {useTranslation} from 'react-i18next';
import {getCategories} from '../../../../../redux/categories/categoriesActions';
import {getPlan, createPlan, updatePlan, linkPriceGroupsToPlan, unlinkPriceGroupsFromPlan} from '../../../../../redux/subscription/subscriptionActions';
import setGlobalState from '../../../../../redux/setGlobalState';
import store from '../../../../../store';
import {combineDateTime} from '../../../../../library/dates';
import {DEFAULT_TIME_ZONE} from '../../../../../globalVariables/locals';
import {createDateAfterValidator} from '../../../../../functions/validationsYup';
import {SubscriptionFormValues} from '../createSubscriptionForm.types';
import {TState} from '../../../../../redux/store.type';
import getColor from '../../../../../functions/getColor';
import useEffectOnUpdate from '../../../../../hooks/useEffectOnUpdate';

interface UseSubscriptionFormProps {
    planTid?: number | null;
}

export const useSubscriptionForm = ({planTid}: UseSubscriptionFormProps) => {
    const {t} = useTranslation('subscriptions');
    const {t: tErrors} = useTranslation('errors');
    const categoriesData = useSelector((state: TState) => state.categories.eventCategories);
    const subscriptionPlans = useSelector((state: TState) => state.subscription.subscriptionPlans);
    const planData = subscriptionPlans?.find(p => p.planTid === planTid);

    useEffectOnUpdate(() => {
        if(!categoriesData?.length) {
            getCategories();
        }
        if(planTid) {
            getPlan(planTid);
        } else {
            setGlobalState('subscription.currentPlan', null);
        }
    }, [planTid]);

    const validationSchema = Yup.object({
        subscriptionType: Yup.string().required(t('subscriptionTypeRequired')),
        subscriptionName: Yup.string().required(t('subscriptionNameRequired')),
        category: Yup.array().min(1, t('categoryRequired')),
        prices: Yup.array().min(1, tErrors('requiredError', {name: t('prices')})),
        ticketsAmount: Yup.number()
            .required(tErrors('requiredError', {name: t('ticketsAmount')}))
            .min(1, tErrors('requiredError', {name: t('ticketsAmount')})),
        ticketsLimit: Yup.number()
            .required(tErrors('requiredError', {name: t('ticketsLimit')}))
            .min(1, tErrors('requiredError', {name: t('ticketsLimit')}))
            .max(Yup.ref('ticketsAmount'), t('ticketsLimitMaxError')),
        credits: Yup.number()
            .required(tErrors('requiredError', {name: t('ticketCredits')}))
            .min(1, tErrors('requiredError', {name: t('ticketCredits')})),
        saleStartDate: Yup.number().nullable().when('activateSaleValidity', {
            is: (activateSaleValidity) => activateSaleValidity === true,
            then: (schema) => schema.required(tErrors('requiredError', {name: t('saleStartDate')}))
        }),
        saleStartTime: Yup.number().nullable().when('activateSaleValidity', {
            is: (activateSaleValidity) => activateSaleValidity === true,
            then: (schema) => schema.required(tErrors('requiredError', {name: t('saleStartTime')}))
        }),
        saleEndDate: Yup.number().nullable().when('activateSaleValidity', {
            is: (activateSaleValidity) => activateSaleValidity === true,
            then: (schema) => schema
                .required(tErrors('requiredError', {name: t('saleEndDate')}))
                .concat(createDateAfterValidator(
                    'saleEndDate',
                    t('saleEndDateBeforeStartDateError'),
                    'saleStartDate',
                    'saleStartTime',
                    'saleEndDate',
                    'saleEndTime'
                ))
        }),
        saleEndTime: Yup.number().nullable().when('activateSaleValidity', {
            is: (activateSaleValidity) => activateSaleValidity === true,
            then: (schema) => schema.required(tErrors('requiredError', {name: t('saleEndTime')}))
        }),
        redemptionEndDate: Yup.number().nullable().when('activateRedemptionValidity', {
            is: (activateRedemptionValidity) => activateRedemptionValidity === true,
            then: (schema) => schema
                .required(tErrors('requiredError', {name: t('redemptionEndDate')}))
                .concat(createDateAfterValidator(
                    'redemptionEndDate',
                    t('redemptionEndDateAfterSaleEndDateError'),
                    'saleEndDate',
                    'saleEndTime',
                    'redemptionEndDate',
                    'redemptionEndTime'
                ))
        }),
        redemptionEndTime: Yup.number().nullable().when('activateRedemptionValidity', {
            is: (activateRedemptionValidity) => activateRedemptionValidity === true,
            then: (schema) => schema.required(tErrors('requiredError', {name: t('redemptionEndTime')}))
        }),
        redemptionUpToPrice: Yup.number()
            .nullable()
            .when('redemptionPrice', {
                is: (redemptionPrice: number) => redemptionPrice > 0,
                then: (schema) => schema.min(Yup.ref('redemptionPrice'), t('endPriceMustBeGreaterThanStartPrice'))
            }),
    });

    const getInitialValues = (): SubscriptionFormValues => {
        // Like in newEvent/globalSteps.js - use the same timestamp for both date and time
        // DateInput and TimeInput with isTimestamp=true will parse it internally
        // Get color - if editing use existing color, otherwise get next available color
        const color = planData?.color || getColor(null, subscriptionPlans);
        return {
            credits: planData?.credits,
            subscriptionType: planData?.planType || 'open',
            subscriptionName: planData?.title || '',
            category: planData?.categories?.map(c => c.catId) || [],
            active: planData?.active !== undefined ? planData.active : true,
            image: planData?.image || null,
            description: planData?.description || '',
            color,
            // Pricing fields
            prices: planData?.prices || [],
            selectedPrices: planData?.selectedPrices || [],
            // Redemption Settings
            redemptionPrice: planData?.redemptionPrice || null,
            redemptionUpToPrice: planData?.redemptionUpToPrice || null,
            priceGroupIds: planData?.priceGroups?.map(pg => pg.spgId) || [],
            // Sale Validity - same timestamp for both date and time, like in newEvent
            saleStartDate: planData?.saleStart || null,
            saleStartTime: planData?.saleStart || null,
            saleEndDate: planData?.saleEnd || null,
            saleEndTime: planData?.saleEnd || null,
            activateSaleValidity: planData?.saleStart || planData?.saleEnd ? true : false,
            // Redemption Validity
            redemptionEndDate: planData?.usageDeadline || null,
            redemptionEndTime: planData?.usageDeadline || null,
            activateRedemptionValidity: planData?.usageDeadline ? true : false,
            // Ticketing Settings
            // fixedBarcode: planData?.fixedBarcode || false,
            // singleTicket: planData?.singleTicket || false,
            // Series Group Assignment
            // seriesGroups: planData?.seriesGroups || []
            // Ticket Amount Settings
            ticketsAmount: planData?.amount,
            ticketsLimit: planData?.limit,
        };
    };

    const handleSubmit = async (values: SubscriptionFormValues) => {
        const planData = {
            title: values.subscriptionName,
            planType: values.subscriptionType,
            active: values.active ? 1 : 0,
            categoryIds: values.category || [],
            image: values.image,
            description: values.description,
            color: values.color,
            // Pricing fields
            prices: values.prices || [],
            selectedPrices: values.selectedPrices || [],
            // Sale Validity
            saleStart: values.activateSaleValidity ? combineDateTime(values.saleStartDate, values.saleStartTime, DEFAULT_TIME_ZONE) : null,
            saleEnd: values.activateSaleValidity ? combineDateTime(values.saleEndDate, values.saleEndTime, DEFAULT_TIME_ZONE) : null,
            usageDeadline: values.activateRedemptionValidity ? combineDateTime(values.redemptionEndDate, values.redemptionEndTime, DEFAULT_TIME_ZONE) : null,
            // Ticket Credits
            credits: values.credits || null,
            // Ticket Amount Settings
            ticketsAmount: values.ticketsAmount || null,
            ticketsLimit: values.ticketsLimit || null,
        };

        let createdPlanTid = planTid;
        let currentPlan = null;
        if(planTid) {
            currentPlan = await updatePlan(planTid, planData);
            createdPlanTid = planTid;
        } else {
            currentPlan = await createPlan(planData);
            createdPlanTid = currentPlan?.planTid;
        }

        // Handle price groups linking/unlinking
        if(createdPlanTid && values.priceGroupIds) {
            // Get current linked price groups
            const currentPriceGroupIds = currentPlan?.plan?.priceGroups?.map(pg => pg.spgId) || [];
            const newPriceGroupIds = values.priceGroupIds || [];

            // Find groups to link (in new but not in current)
            const toLink = newPriceGroupIds.filter((id: number) => !currentPriceGroupIds.includes(id));
            // Find groups to unlink (in current but not in new)
            const toUnlink = currentPriceGroupIds.filter((id: number) => !newPriceGroupIds.includes(id));

            // Link new groups and unlink removed groups in parallel (batch operations)
            const promises = [];
            if(toLink.length > 0) {
                promises.push(linkPriceGroupsToPlan(createdPlanTid, toLink));
            }
            if(toUnlink.length > 0) {
                promises.push(unlinkPriceGroupsFromPlan(createdPlanTid, toUnlink));
            }
            if(promises.length > 0) {
                await Promise.all(promises);
            }
        }
    };

    return {
        validationSchema,
        getInitialValues,
        handleSubmit
    };
};

